package com.evoapps.evoshift.ui.richtext

import androidx.compose.foundation.background
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.FormatIndentDecrease
import androidx.compose.material.icons.automirrored.filled.FormatIndentIncrease
import androidx.compose.material.icons.automirrored.filled.FormatListBulleted
import androidx.compose.material.icons.filled.BorderColor
import androidx.compose.material.icons.filled.FormatBold
import androidx.compose.material.icons.filled.FormatColorReset
import androidx.compose.material.icons.filled.FormatColorText
import androidx.compose.material.icons.filled.FormatItalic
import androidx.compose.material.icons.filled.FormatListNumbered
import androidx.compose.material.icons.filled.FormatSize
import androidx.compose.material.icons.filled.FormatStrikethrough
import androidx.compose.material.icons.filled.FormatUnderlined
import androidx.compose.material.icons.filled.Link
import androidx.compose.material3.DropdownMenu
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.IconButtonDefaults
import androidx.compose.material3.IconToggleButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Text
import androidx.compose.material3.Typography
import androidx.compose.runtime.Composable
import androidx.compose.runtime.Stable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.focus.FocusRequester
import androidx.compose.ui.focus.focusRequester
import androidx.compose.ui.focus.onFocusChanged
import androidx.compose.ui.input.key.Key
import androidx.compose.ui.input.key.KeyEvent
import androidx.compose.ui.input.key.KeyEventType
import androidx.compose.ui.input.key.isCtrlPressed
import androidx.compose.ui.input.key.isMetaPressed
import androidx.compose.ui.input.key.isShiftPressed
import androidx.compose.ui.input.key.key
import androidx.compose.ui.input.key.onPreviewKeyEvent
import androidx.compose.ui.input.key.type
import androidx.compose.ui.semantics.contentDescription
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.text.AnnotatedString
import androidx.compose.ui.text.SpanStyle
import androidx.compose.ui.text.buildAnnotatedString
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.KeyboardCapitalization
import androidx.compose.ui.text.input.OffsetMapping
import androidx.compose.ui.text.input.TransformedText
import androidx.compose.ui.text.input.VisualTransformation
import androidx.compose.ui.unit.dp
import com.evoapps.evoshift.domain.model.RichColor
import com.evoapps.evoshift.domain.model.RichListKind
import com.evoapps.evoshift.domain.model.RichMark
import com.evoapps.evoshift.domain.model.RichSize
import com.evoapps.evoshift.domain.model.RichSpan
import com.evoapps.evoshift.ui.adaptive.handCursor
import com.evoapps.evoshift.widgets.LocalOnColours
import com.evoapps.evoshift.widgets.OnColours
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.rich_bold
import evoshift.composeapp.generated.resources.rich_color
import evoshift.composeapp.generated.resources.rich_color_blue
import evoshift.composeapp.generated.resources.rich_color_green
import evoshift.composeapp.generated.resources.rich_color_grey
import evoshift.composeapp.generated.resources.rich_color_none
import evoshift.composeapp.generated.resources.rich_color_orange
import evoshift.composeapp.generated.resources.rich_color_purple
import evoshift.composeapp.generated.resources.rich_color_red
import evoshift.composeapp.generated.resources.rich_highlight
import evoshift.composeapp.generated.resources.rich_indent
import evoshift.composeapp.generated.resources.rich_italic
import evoshift.composeapp.generated.resources.rich_link
import evoshift.composeapp.generated.resources.rich_list_bullet
import evoshift.composeapp.generated.resources.rich_list_lettered
import evoshift.composeapp.generated.resources.rich_list_numbered
import evoshift.composeapp.generated.resources.rich_outdent
import evoshift.composeapp.generated.resources.rich_size
import evoshift.composeapp.generated.resources.rich_size_heading
import evoshift.composeapp.generated.resources.rich_size_large
import evoshift.composeapp.generated.resources.rich_size_normal
import evoshift.composeapp.generated.resources.rich_size_small
import evoshift.composeapp.generated.resources.rich_strikethrough
import evoshift.composeapp.generated.resources.rich_toolbar
import evoshift.composeapp.generated.resources.rich_underline
import org.jetbrains.compose.resources.StringResource
import org.jetbrains.compose.resources.stringResource

/*
 * SH-211: the lesson editor. No rich-text library: the one KMP editor for Compose (Compose Rich
 * Editor) stores HTML or Markdown and is built for older Compose versions, and a lesson must be
 * stored as RichText's JSON and render the same everywhere. So a Material text field edits the
 * plain text, [RichEditorState] keeps the styles beside it, and a visual transformation draws
 * them (with the list markers) in the field. docs/training.md "Formatted lessons".
 */

/** The text colours a lesson offers, in the toolbar's order, with their names. */
val RichColorLabels: List<Pair<RichColor, StringResource>> = listOf(
    RichColor.RED to Res.string.rich_color_red,
    RichColor.ORANGE to Res.string.rich_color_orange,
    RichColor.GREEN to Res.string.rich_color_green,
    RichColor.BLUE to Res.string.rich_color_blue,
    RichColor.PURPLE to Res.string.rich_color_purple,
    RichColor.GREY to Res.string.rich_color_grey
)

val RichSizeLabels: List<Pair<RichSize, StringResource>> = listOf(
    RichSize.SMALL to Res.string.rich_size_small,
    RichSize.NORMAL to Res.string.rich_size_normal,
    RichSize.LARGE to Res.string.rich_size_large,
    RichSize.HEADING to Res.string.rich_size_heading
)

/**
 * The editors of one screen: which is in use (the toolbar acts on it) and which asked for the
 * link dialog. The screen shows the toolbar ([RichToolbar]) above the keyboard on phones and
 * above the field on wide screens.
 */
@Stable
class RichEditorHost {
    /** The editor last focused (null once none is). */
    var active: RichEditorState? by mutableStateOf(null)
    /** The editor whose link is being edited. */
    var linking: RichEditorState? by mutableStateOf(null)
    internal val focus = HashMap<RichEditorState, FocusRequester>()

    /** Gives the field back its focus (after the link dialog). */
    fun refocus(state: RichEditorState) {
        runCatching { focus[state]?.requestFocus() }
    }
}

@Composable
fun rememberRichEditorHost(): RichEditorHost = remember { RichEditorHost() }

/**
 * The field of a formatted text, labelled [label]. Desktop and web shortcuts (Ctrl, or ⌘ on a
 * Mac): B, I, U bold, italic, underline; Shift+X strikethrough, Shift+H highlight; K a link;
 * Shift+7 numbered, Shift+8 bulleted, Shift+9 lettered list; Tab and Shift+Tab nest list items.
 */
@Composable
fun RichTextEditor(state: RichEditorState, host: RichEditorHost, label: String, modifier: Modifier = Modifier, minLines: Int = 4) {
    val on = LocalOnColours.current
    val typography = MaterialTheme.typography
    val snapshot = state.snapshot()
    val transformation = remember(snapshot, on, typography) { RichVisualTransformation(snapshot, on, typography) }
    val requester = remember { FocusRequester() }
    host.focus[state] = requester
    OutlinedTextField(
        value = state.value,
        onValueChange = state::onValueChange,
        label = { Text(label) },
        visualTransformation = transformation,
        minLines = minLines,
        keyboardOptions = KeyboardOptions(capitalization = KeyboardCapitalization.Sentences),
        modifier = modifier
            .focusRequester(requester)
            .onFocusChanged { focus ->
                if (focus.isFocused) host.active = state
                else if (host.active === state && host.linking == null) host.active = null
            }
            .onPreviewKeyEvent { event -> handleShortcut(event, state, host) }
    )
}

/** The editor's keyboard shortcuts; true when [event] was one. */
fun handleShortcut(event: KeyEvent, state: RichEditorState, host: RichEditorHost): Boolean {
    if (event.type != KeyEventType.KeyDown) return false
    val command = event.isCtrlPressed || event.isMetaPressed
    val shift = event.isShiftPressed
    when {
        command && !shift && event.key == Key.B -> state.toggle(RichMark.BOLD)
        command && !shift && event.key == Key.I -> state.toggle(RichMark.ITALIC)
        command && !shift && event.key == Key.U -> state.toggle(RichMark.UNDERLINE)
        command && shift && event.key == Key.X -> state.toggle(RichMark.STRIKETHROUGH)
        command && shift && event.key == Key.H -> state.toggle(RichMark.HIGHLIGHT)
        command && !shift && event.key == Key.K -> host.linking = state
        command && shift && event.key == Key.Seven -> state.toggleList(RichListKind.NUMBERED)
        command && shift && event.key == Key.Eight -> state.toggleList(RichListKind.BULLET)
        command && shift && event.key == Key.Nine -> state.toggleList(RichListKind.LETTERED)
        !command && event.key == Key.Tab && state.currentList() != null -> if (shift) state.outdent() else state.indent()
        else -> return false
    }
    return true
}

/**
 * Draws [snapshot]'s styles, sizes and list markers in the field. The markers are inserted
 * characters, so the offsets are mapped; anything else keeps the field's own text.
 */
private data class RichVisualTransformation(val snapshot: RichSnapshot, val on: OnColours, val typography: Typography) : VisualTransformation {
    override fun filter(text: AnnotatedString): TransformedText {
        if (text.text != snapshot.text) return TransformedText(text, OffsetMapping.Identity)
        val parts = snapshot.text.split('\n')
        val markers = snapshot.markers
        val starts = IntArray(parts.size)
        val prefixes = IntArray(parts.size)
        var at = 0
        parts.forEachIndexed { i, part -> starts[i] = at; at += part.length + 1 }
        val out = buildAnnotatedString {
            parts.forEachIndexed { i, part ->
                val line = snapshot.lines.getOrElse(i) { RichEditorState.Line() }
                val prefix = markers.getOrNull(i)?.let { "    ".repeat(line.level) + it + " " }.orEmpty()
                prefixes[i] = prefix.length
                if (prefix.isNotEmpty()) {
                    val start = length
                    append(prefix)
                    addStyle(SpanStyle(color = on.onSurfaceVariant), start, length)
                }
                val contentStart = length
                append(part)
                val style = richTextStyle(line.size, typography)
                if (line.size != RichSize.NORMAL) {
                    addStyle(SpanStyle(fontSize = style.fontSize, fontWeight = style.fontWeight), contentStart - prefix.length, length)
                }
                val lineSpans = snapshot.spans.mapNotNull { span ->
                    val s = maxOf(span.start, starts[i]) - starts[i]
                    val e = minOf(span.end, starts[i] + part.length) - starts[i]
                    if (s < e) RichSpan(s, e, span.style) else null
                }
                addRichSpans(lineSpans, part.length, contentStart, on.dark, on.link, clickable = false)
                if (i < parts.lastIndex) append('\n')
            }
        }
        val cumulative = IntArray(parts.size + 1)
        for (i in parts.indices) cumulative[i + 1] = cumulative[i] + prefixes[i]
        val length = snapshot.text.length
        val outLength = out.length
        val mapping = object : OffsetMapping {
            override fun originalToTransformed(offset: Int): Int {
                val o = offset.coerceIn(0, length)
                var line = 0
                while (line + 1 < parts.size && starts[line + 1] <= o) line++
                return (o + cumulative[line + 1]).coerceIn(0, outLength)
            }

            override fun transformedToOriginal(offset: Int): Int {
                val t = offset.coerceIn(0, outLength)
                var line = 0
                while (line + 1 < parts.size && starts[line + 1] + cumulative[line + 1] <= t) line++
                val contentStart = starts[line] + cumulative[line + 1]
                return (if (t < contentStart) starts[line] else t - cumulative[line + 1]).coerceIn(0, length)
            }
        }
        return TransformedText(out, mapping)
    }
}

/**
 * The formatting toolbar of [state]: marks, colour, size, lists and nesting, link. It scrolls
 * sideways when the screen is narrow; every button has its name for screen readers and tests.
 */
@Composable
fun RichToolbar(state: RichEditorState, host: RichEditorHost, modifier: Modifier = Modifier) {
    val description = stringResource(Res.string.rich_toolbar)
    Row(
        modifier
            .fillMaxWidth()
            .background(MaterialTheme.colorScheme.surfaceVariant, RoundedCornerShape(12.dp))
            .semantics { contentDescription = description }
            .horizontalScroll(rememberScrollState())
            .padding(horizontal = 4.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        MarkButton(state, RichMark.BOLD, Res.string.rich_bold) { Icon(Icons.Default.FormatBold, null) }
        MarkButton(state, RichMark.ITALIC, Res.string.rich_italic) { Icon(Icons.Default.FormatItalic, null) }
        MarkButton(state, RichMark.UNDERLINE, Res.string.rich_underline) { Icon(Icons.Default.FormatUnderlined, null) }
        MarkButton(state, RichMark.STRIKETHROUGH, Res.string.rich_strikethrough) { Icon(Icons.Default.FormatStrikethrough, null) }
        MarkButton(state, RichMark.HIGHLIGHT, Res.string.rich_highlight) { Icon(Icons.Default.BorderColor, null) }
        ColorButton(state)
        SizeButton(state)
        ListButton(state, RichListKind.NUMBERED, Res.string.rich_list_numbered) { Icon(Icons.Default.FormatListNumbered, null) }
        ListButton(state, RichListKind.LETTERED, Res.string.rich_list_lettered) { Text("a)", fontWeight = FontWeight.Bold) }
        ListButton(state, RichListKind.BULLET, Res.string.rich_list_bullet) { Icon(Icons.AutoMirrored.Filled.FormatListBulleted, null) }
        ToolButton(Res.string.rich_outdent, enabled = state.canOutdent, onClick = state::outdent) { Icon(Icons.AutoMirrored.Filled.FormatIndentDecrease, null) }
        ToolButton(Res.string.rich_indent, enabled = state.canIndent, onClick = state::indent) { Icon(Icons.AutoMirrored.Filled.FormatIndentIncrease, null) }
        ToolButton(Res.string.rich_link, onClick = { host.linking = state }) { Icon(Icons.Default.Link, null) }
    }
}

@Composable
private fun toggleColors() = IconButtonDefaults.iconToggleButtonColors(
    contentColor = MaterialTheme.colorScheme.onSurfaceVariant,
    checkedContainerColor = MaterialTheme.colorScheme.primary,
    checkedContentColor = MaterialTheme.colorScheme.onPrimary
)

@Composable
private fun Toggle(checked: Boolean, label: StringResource, onClick: () -> Unit, content: @Composable () -> Unit) {
    val name = stringResource(label)
    IconToggleButton(
        checked = checked,
        onCheckedChange = { onClick() },
        colors = toggleColors(),
        modifier = Modifier.size(44.dp).handCursor().semantics { contentDescription = name }
    ) { content() }
}

@Composable
private fun MarkButton(state: RichEditorState, mark: RichMark, label: StringResource, content: @Composable () -> Unit) =
    Toggle(state.isActive(mark), label, { state.toggle(mark) }, content)

@Composable
private fun ListButton(state: RichEditorState, kind: RichListKind, label: StringResource, content: @Composable () -> Unit) =
    Toggle(state.currentList() == kind, label, { state.toggleList(kind) }, content)

@Composable
private fun ToolButton(label: StringResource, enabled: Boolean = true, onClick: () -> Unit, content: @Composable () -> Unit) {
    val name = stringResource(label)
    IconButton(
        onClick = onClick, enabled = enabled,
        colors = IconButtonDefaults.iconButtonColors(contentColor = MaterialTheme.colorScheme.onSurfaceVariant),
        modifier = Modifier.size(44.dp).handCursor().semantics { contentDescription = name }
    ) { content() }
}

@Composable
private fun ColorButton(state: RichEditorState) {
    var open by remember { mutableStateOf(false) }
    val current = state.currentColor()
    val dark = LocalOnColours.current.dark
    Box {
        ToolButton(Res.string.rich_color, onClick = { open = true }) {
            Icon(Icons.Default.FormatColorText, null, tint = current?.let { RichPalette.color(it, dark) } ?: MaterialTheme.colorScheme.onSurfaceVariant)
        }
        DropdownMenu(expanded = open, onDismissRequest = { open = false }) {
            val menuDark = LocalOnColours.current.dark
            DropdownMenuItem(
                text = { Text(stringResource(Res.string.rich_color_none)) },
                leadingIcon = { Icon(Icons.Default.FormatColorReset, null) },
                onClick = { open = false; state.setColor(null) },
                modifier = Modifier.handCursor()
            )
            RichColorLabels.forEach { (color, label) ->
                DropdownMenuItem(
                    text = { Text(stringResource(label), color = RichPalette.color(color, menuDark), fontWeight = if (color == current) FontWeight.Bold else null) },
                    leadingIcon = { Icon(Icons.Default.FormatColorText, null, tint = RichPalette.color(color, menuDark)) },
                    onClick = { open = false; state.setColor(color) },
                    modifier = Modifier.handCursor()
                )
            }
        }
    }
}

@Composable
private fun SizeButton(state: RichEditorState) {
    var open by remember { mutableStateOf(false) }
    val current = state.currentSize()
    val typography = MaterialTheme.typography
    Box {
        ToolButton(Res.string.rich_size, onClick = { open = true }) { Icon(Icons.Default.FormatSize, null) }
        DropdownMenu(expanded = open, onDismissRequest = { open = false }) {
            RichSizeLabels.forEach { (size, label) ->
                val style = richTextStyle(size, typography)
                DropdownMenuItem(
                    text = { Text(stringResource(label), style = style, fontWeight = if (size == current) FontWeight.Bold else style.fontWeight) },
                    onClick = { open = false; state.setSize(size) },
                    modifier = Modifier.handCursor()
                )
            }
        }
    }
}
